import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import NotificationBell from '@/components/feature/NotificationBell';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
);

const ITEMS_PER_PAGE = 20;

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  visible: boolean;
}

interface PendingSaleRecord {
  id: string;
  customer_name: string;
  customer_phone: string;
  quantity: number;
  total_amount: number;
  delivery_address?: string;
  delivery_day?: string;
  delivery_time?: string;
  created_at: string;
  status: string;
}

export default function SalesPage() {
  const navigate = useNavigate();
  const [sales, setSales] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSale, setEditingSale] = useState<any>(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_type: 'occasional',
    quantity: 1,
    unit_price: 17000,
    payment_method: 'Efectivo',
    payment_status: 'paid',
    payment_due_date: '',
    notes: '',
    source: 'manual',
    register_as_customer: false,
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    customer_document_id: '',
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<any>(null);
  const [filterPeriod, setFilterPeriod] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [periodStartDate, setPeriodStartDate] = useState<string>('');
  const [periodEndDate, setPeriodEndDate] = useState<string>('');
  const [todaySales, setTodaySales] = useState(0);
  const [todayBottles, setTodayBottles] = useState(0);
  const [monthSales, setMonthSales] = useState(0);
  const [totalBottles, setTotalBottles] = useState(0);
  const [totalSalesGlobal, setTotalSalesGlobal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState>({ message: '', type: 'info', visible: false });
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [pendingSalesList, setPendingSalesList] = useState<PendingSaleRecord[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [customerBottleCount, setCustomerBottleCount] = useState<number | null>(null);
  const [isLoadingBottleCount, setIsLoadingBottleCount] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState<{customerName: string; totalBottles: number; customerId: string} | null>(null);
  const [showNextSaleBanner, setShowNextSaleBanner] = useState<{customerName: string; customerId: string} | null>(null);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [pageSearchTerm, setPageSearchTerm] = useState('');
  const [pageFilteredCustomers, setPageFilteredCustomers] = useState<any[]>([]);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [modalFilteredCustomers, setModalFilteredCustomers] = useState<any[]>([]);
  const [customerPromoCounts, setCustomerPromoCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    const searchCustomers = async () => {
      if (pageSearchTerm.trim() === '') {
        setPageFilteredCustomers([]);
        return;
      }
      const term = pageSearchTerm.trim();
      // Primero buscar en tabla customers
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .or(`name.ilike.%${term}%,phone.ilike.%${term}%,document_id.ilike.%${term}%`)
        .order('name')
        .limit(50);

      if (customerError) {
        console.error('Error buscando clientes:', customerError);
      }

      // También buscar nombres únicos en ventas históricas que no estén en customers
      const { data: salesData, error: salesError } = await supabase
        .from('sales')
        .select('customer_name, customer_phone, customer_type')
        .ilike('customer_name', `%${term}%`)
        .order('customer_name')
        .limit(50);

      if (salesError) {
        console.error('Error buscando en ventas:', salesError);
      }

      // Combinar resultados: priorizar customers formales
      const combined = new Map();
      (customerData || []).forEach((c) => {
        combined.set(c.id, { ...c, source: 'customer' });
      });

      // Agregar clientes de ventas que no estén en customers
      (salesData || []).forEach((s) => {
        const key = s.customer_name?.trim()?.toLowerCase();
        if (!key) return;
        const alreadyExists = Array.from(combined.values()).some(
          (c) => c.name?.trim()?.toLowerCase() === key,
        );
        if (!alreadyExists) {
          combined.set(`sale-${key}`, {
            id: null,
            name: s.customer_name,
            phone: s.customer_phone || '',
            customer_type: s.customer_type || 'occasional',
            source: 'sale',
          });
        }
      });

      setPageFilteredCustomers(Array.from(combined.values()));
    };
    searchCustomers();
  }, [pageSearchTerm]);

  useEffect(() => {
    if (modalSearchTerm.trim() === '') {
      setModalFilteredCustomers([]);
    } else {
      const term = modalSearchTerm.toLowerCase().trim();
      const filtered = customers.filter((c) =>
        c.name.toLowerCase().includes(term) ||
        (c.phone && c.phone.includes(term)) ||
        (c.document_id && c.document_id.includes(term)),
      );
      setModalFilteredCustomers(filtered);
    }
  }, [modalSearchTerm, customers]);

  useEffect(() => {
    if (!loading) {
      loadSalesWithPagination();
      if (filterDate) {
        loadStats(filterDate);
      } else {
        loadStats();
      }
      checkPendingPayments();
    }
  }, [currentPage, filterStatus, searchQuery, filterType, filterPaymentMethod, filterDate, filterPeriod, loading]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    } else {
      await loadCustomers();
      await loadStats();
      await loadSalesWithPagination();
      await checkPendingPayments();
    }
    setLoading(false);
  };

  const loadCustomers = async () => {
    const { data } = await supabase.from('customers').select('*').order('name').limit(5000);
    setCustomers(data || []);
  };

  const loadSalesWithPagination = async () => {
    const from = (currentPage - 1) * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    let query = supabase
      .from('sales')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    // Filtro por período rápido
    if (filterPeriod !== 'all') {
      const now = new Date();
      let periodStart: Date;
      let periodEnd: Date;

      if (filterPeriod === 'today') {
        periodStart = new Date(now);
        periodStart.setHours(0, 0, 0, 0);
        periodEnd = new Date(periodStart);
        periodEnd.setDate(periodEnd.getDate() + 1);
      } else if (filterPeriod === 'week') {
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        periodStart = new Date(now);
        periodStart.setDate(diff);
        periodStart.setHours(0, 0, 0, 0);
        periodEnd = new Date(periodStart);
        periodEnd.setDate(periodEnd.getDate() + 7);
      } else if (filterPeriod === 'month') {
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
        periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      } else {
        periodStart = new Date(now);
        periodEnd = new Date(now);
      }

      setPeriodStartDate(periodStart.toISOString().split('T')[0]);
      setPeriodEndDate(periodEnd.toISOString().split('T')[0]);
      query = query.gte('created_at', periodStart.toISOString()).lt('created_at', periodEnd.toISOString());
    } else {
      setPeriodStartDate('');
      setPeriodEndDate('');
    }

    if (filterDate) {
      const startOfDay = new Date(filterDate + 'T00:00:00');
      const endOfDay = new Date(filterDate + 'T00:00:00');
      endOfDay.setDate(endOfDay.getDate() + 1);
      query = query.gte('created_at', startOfDay.toISOString()).lt('created_at', endOfDay.toISOString());
    }

    if (filterStatus !== 'all') {
      query = query.eq('status', filterStatus);
    }

    const { data, count } = await query.range(from, to);

    // Aplicar filtros en el cliente
    let filteredData = data || [];

    // Filtrar por búsqueda
    if (searchQuery.trim() !== '') {
      const searchLower = searchQuery.toLowerCase();
      filteredData = filteredData.filter((sale) =>
        sale.customer_name?.toLowerCase().includes(searchLower) ||
        sale.customer_phone?.includes(searchQuery)
      );
    }

    // Filtrar por tipo de cliente
    if (filterType !== 'all') {
      filteredData = filteredData.filter((sale) => sale.customer_type === filterType);
    }

    // Filtrar por método de pago
    if (filterPaymentMethod !== 'all') {
      if (filterPaymentMethod === 'cash') {
        filteredData = filteredData.filter((sale) => sale.payment_method === 'Efectivo');
      } else if (filterPaymentMethod === 'transfer') {
        filteredData = filteredData.filter((sale) => sale.payment_method === 'Transferencia');
      } else if (filterPaymentMethod === 'credit') {
        filteredData = filteredData.filter((sale) => sale.payment_method === 'Crédito');
      }
    }

    setSales(filteredData);
    setTotalSales(count || 0);
    await loadCustomerPromoCounts(filteredData);
  };

  const loadCustomerPromoCounts = async (salesList: any[]) => {
    const customerIds = [...new Set(salesList.filter(s => s.customer_id).map(s => s.customer_id))];
    if (customerIds.length === 0) {
      setCustomerPromoCounts({});
      return;
    }
    const { data } = await supabase
      .from('sales')
      .select('customer_id, quantity')
      .in('customer_id', customerIds)
      .eq('counts_toward_promotion', true)
      .neq('status', 'cancelled')
      .gt('total_amount', 0);
    if (data) {
      const counts: Record<string, number> = {};
      data.forEach(s => {
        if (s.customer_id) {
          counts[s.customer_id] = (counts[s.customer_id] || 0) + (parseInt(s.quantity) || 0);
        }
      });
      setCustomerPromoCounts(counts);
    }
  };

  const loadStats = async (dateFilter?: string) => {
    const now = new Date();
    let periodStart: Date;
    let periodEnd: Date;

    if (filterPeriod === 'today') {
      periodStart = new Date(now);
      periodStart.setHours(0, 0, 0, 0);
      periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + 1);
    } else if (filterPeriod === 'week') {
      const dayOfWeek = now.getDay();
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      periodStart = new Date(now);
      periodStart.setDate(diff);
      periodStart.setHours(0, 0, 0, 0);
      periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + 7);
    } else if (filterPeriod === 'month') {
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    } else {
      periodStart = dateFilter ? new Date(dateFilter + 'T00:00:00') : new Date();
      if (!dateFilter) periodStart.setHours(0, 0, 0, 0);
      periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + 1);
    }

    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const monthStart = new Date(currentYear, currentMonth, 1);
    const monthEnd = new Date(currentYear, currentMonth + 1, 1);

    // Ventas del período seleccionado (o hoy si no hay período)
    const { data: periodData } = await supabase
      .from('sales')
      .select('total_amount')
      .gte('created_at', periodStart.toISOString())
      .lt('created_at', periodEnd.toISOString());
    const periodTotal = periodData?.reduce((sum, sale) => sum + parseFloat(sale.total_amount), 0) || 0;
    setTodaySales(periodTotal);

    // Botellones del período seleccionado
    const { data: periodBottlesData } = await supabase
      .from('sales')
      .select('quantity')
      .gte('created_at', periodStart.toISOString())
      .lt('created_at', periodEnd.toISOString());
    const periodBottles = periodBottlesData?.reduce((sum, sale) => sum + parseInt(sale.quantity || 0), 0) || 0;
    setTodayBottles(periodBottles);

    // Ventas del mes (siempre)
    const { data: monthData } = await supabase
      .from('sales')
      .select('total_amount')
      .gte('created_at', monthStart.toISOString())
      .lt('created_at', monthEnd.toISOString());
    const monthTotal = monthData?.reduce((sum, sale) => sum + parseFloat(sale.total_amount), 0) || 0;
    setMonthSales(monthTotal);

    // Botellones del mes
    const { data: bottlesData } = await supabase
      .from('sales')
      .select('quantity')
      .gte('created_at', monthStart.toISOString())
      .lt('created_at', monthEnd.toISOString());
    const monthBottles = bottlesData?.reduce((sum, sale) => sum + parseInt(sale.quantity || 0), 0) || 0;
    setTotalBottles(monthBottles);

    const { count } = await supabase
      .from('sales')
      .select('*', { count: 'exact', head: true });
    setTotalSalesGlobal(count || 0);
  };

  const checkPendingPayments = async () => {
    const today = new Date().toISOString().split('T')[0];

    const { data } = await supabase
      .from('sales')
      .select('*')
      .eq('payment_status', 'pending')
      .lte('payment_due_date', today)
      .order('payment_due_date', { ascending: true });

    if (data && data.length > 0) {
      setPendingPayments(data);
    }
  };

  const fetchPendingSales = async () => {
    setLoadingNotifications(true);
    const { data, error } = await supabase
      .from('sales')
      .select('id, customer_name, customer_phone, quantity, total_amount, delivery_address, delivery_day, delivery_time, created_at, status')
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(10);

    if (!error && data) {
      setPendingSalesList(data);
    }
    setLoadingNotifications(false);
  };

  const handleBellClick = () => {
    if (!showNotificationPanel) {
      fetchPendingSales();
    }
    setShowNotificationPanel(!showNotificationPanel);
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterByPending = () => {
    setFilterStatus('pending');
    setShowNotificationPanel(false);
  };

  const countCustomerBottles = async (customerId: string) => {
    setIsLoadingBottleCount(true);
    try {
      const { data, error } = await supabase
        .from('sales')
        .select('quantity')
        .eq('customer_id', customerId)
        .eq('counts_toward_promotion', true)
        .neq('status', 'cancelled')
        .gt('total_amount', 0);
      if (error) {
        console.error('Error counting bottles:', error);
        setCustomerBottleCount(null);
        return;
      }
      const total = (data || []).reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
      setCustomerBottleCount(total);
    } finally {
      setIsLoadingBottleCount(false);
    }
  };

  const countBottlesByNamePhone = async (name: string, phone: string) => {
    setIsLoadingBottleCount(true);
    try {
      let query = supabase
        .from('sales')
        .select('quantity')
        .eq('counts_toward_promotion', true)
        .neq('status', 'cancelled')
        .gt('total_amount', 0);
      if (name.trim()) {
        query = query.ilike('customer_name', `%${name.trim()}%`);
      }
      if (phone.trim()) {
        query = query.eq('customer_phone', phone.trim());
      }
      const { data, error } = await query;
      if (error) {
        console.error('Error counting bottles by name/phone:', error);
        setCustomerBottleCount(null);
        return;
      }
      const total = (data || []).reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
      setCustomerBottleCount(total);
    } finally {
      setIsLoadingBottleCount(false);
    }
  };

  const handleCustomerSelect = (customer: any) => {
    let price = 17000;
    if (customer.customer_type === 'store') price = 12000;
    if (customer.customer_type === 'occasional_18000') price = 18000;

    setFormData({
      ...formData,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_type: customer.customer_type,
      unit_price: price,
      quantity: customer.customer_type === 'store' ? 10 : 1,
      register_as_customer: false,
      customer_phone: customer.phone || '',
      customer_email: customer.email || '',
      customer_address: customer.address || '',
      customer_document_id: customer.document_id || '',
    });
    setModalSearchTerm(customer.name);
    setModalFilteredCustomers([]);
    countCustomerBottles(customer.id);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (type: string) => {
    setFilterType(type);
  };

  const handlePaymentFilterChange = (payment: string) => {
    setFilterPaymentMethod(payment);
  };

  const handlePeriodFilterChange = (period: 'all' | 'today' | 'week' | 'month') => {
    setFilterPeriod(period);
    setFilterDate('');
    setCurrentPage(1);
  };

  const handleDateFilterChange = (date: string) => {
    setFilterDate(date);
    setFilterPeriod('all');
    if (date) {
      loadStats(date);
    } else {
      loadStats();
    }
  };

  const handleEdit = (sale: any) => {
    setEditingSale(sale);
    const customer = customers.find((c) => c.id === sale.customer_id);

    setFormData({
      customer_id: sale.customer_id || '',
      customer_name: sale.customer_name || '',
      customer_type: sale.customer_type || 'occasional',
      quantity: sale.quantity || 1,
      unit_price: sale.unit_price || 17000,
      payment_method: sale.payment_method || 'Efectivo',
      payment_status: sale.payment_status || 'paid',
      payment_due_date: sale.payment_due_date || '',
      notes: sale.notes || '',
      source: sale.source || 'manual',
      register_as_customer: false,
      customer_phone: customer?.phone || sale.customer_phone || '',
      customer_email: customer?.email || '',
      customer_address: customer?.address || '',
      customer_document_id: customer?.document_id || '',
    });

    if (customer) {
      setModalSearchTerm(customer.name);
      setCustomerBottleCount(null);
      countCustomerBottles(customer.id);
    } else {
      setModalSearchTerm(sale.customer_name || '');
    }

    setModalFilteredCustomers([]);
    setShowNextSaleBanner(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validaciones
      if (formData.customer_type === 'store' && formData.quantity < 10) {
        showToast('Las tiendas deben comprar mínimo 10 botellones para acceder al precio especial de $12,000.', 'warning');
        return;
      }

      if (formData.payment_method === 'Crédito' && !formData.payment_due_date) {
        showToast('Debes especificar la fecha de pago para ventas a crédito.', 'warning');
        return;
      }

      if (formData.register_as_customer) {
        if (!formData.customer_name?.trim()) {
          showToast('Debes ingresar el nombre completo del cliente.', 'warning');
          return;
        }
        if (!formData.customer_phone?.trim()) {
          showToast('Debes ingresar el teléfono del cliente.', 'warning');
          return;
        }
      }

      if (
        formData.customer_type !== 'occasional' && formData.customer_type !== 'occasional_18000' &&
        !formData.register_as_customer &&
        !formData.customer_id &&
        (!modalSearchTerm?.trim())
      ) {
        showToast('Debes buscar y seleccionar un cliente o marcar la opción de registrar un nuevo cliente.', 'warning');
        return;
      }

      if (formData.customer_type === 'occasional' || formData.customer_type === 'occasional_18000') {
        if (!formData.customer_name?.trim()) {
          showToast('Debes ingresar el nombre del cliente.', 'warning');
          return;
        }
        if (!formData.customer_phone?.trim()) {
          showToast('Debes ingresar el teléfono del cliente.', 'warning');
          return;
        }
      }

      const total_amount = formData.quantity * formData.unit_price;

      // Siempre usar el nombre que escribió el usuario en el formulario
      const saleCustomerName = formData.customer_name?.trim() || modalSearchTerm?.trim() || '';

      if (!saleCustomerName) {
        showToast('Error: el nombre del cliente está vacío.', 'error');
        return;
      }

      // Edición
      if (editingSale) {
        const { error: saleError } = await supabase
          .from('sales')
          .update({
            customer_name: saleCustomerName,
            customer_type: formData.customer_type,
            customer_phone: formData.customer_phone?.trim() || null,
            quantity: formData.quantity,
            unit_price: formData.unit_price,
            total_amount,
            payment_method: formData.payment_method,
            payment_status:
              formData.payment_method === 'Crédito' ? 'pending' : formData.payment_status,
            payment_due_date:
              formData.payment_method === 'Crédito' ? formData.payment_due_date : null,
            notes: formData.notes?.trim() || null,
          })
          .eq('id', editingSale.id);

        if (saleError) {
          console.error('Error al actualizar venta:', saleError);
          showToast('Error al actualizar la venta: ' + saleError.message, 'error');
          return;
        }

        setShowModal(false);
        setEditingSale(null);
        setModalSearchTerm('');
        setModalFilteredCustomers([]);
        setFormData({
          customer_id: '',
          customer_name: '',
          customer_type: 'occasional',
          quantity: 1,
          unit_price: 17000,
          payment_method: 'Efectivo',
          payment_status: 'paid',
          payment_due_date: '',
          notes: '',
          source: 'manual',
          register_as_customer: false,
          customer_phone: '',
          customer_email: '',
          customer_address: '',
          customer_document_id: '',
        });
        setFilterStatus('all');
        setSearchQuery('');
        setFilterType('all');
        setFilterPaymentMethod('all');
        setFilterDate('');
        setCurrentPage(1);
        await loadStats();
        await loadSalesWithPagination();
        await checkPendingPayments();
        showToast('¡Venta actualizada exitosamente!', 'success');
        return;
      }

      // Registro nuevo cliente si corresponde
      let finalCustomerId = formData.customer_id;
      let verifiedCustomer = null;

      if (!formData.customer_id && (formData.register_as_customer || formData.customer_type === 'occasional' || formData.customer_type === 'occasional_18000')) {
        const { data: existingCustomer, error: findError } = await supabase
          .from('customers')
          .select('*')
          .eq('phone', formData.customer_phone.trim())
          .maybeSingle();

        if (existingCustomer) {
          finalCustomerId = existingCustomer.id;
          verifiedCustomer = existingCustomer;
        } else {
          let price = 17000;
          if (formData.customer_type === 'store') price = 12000;
          if (formData.customer_type === 'occasional_18000') price = 18000;

          const { data: newCustomer, error: customerError } = await supabase
            .from('customers')
            .insert([{
              name: saleCustomerName,
              phone: formData.customer_phone.trim(),
              email: formData.customer_email?.trim() || null,
              address: formData.customer_address?.trim() || null,
              document_id: formData.customer_document_id?.trim() || null,
              customer_type: formData.customer_type === 'occasional_18000' ? 'occasional' : formData.customer_type,
              subscription_price: price,
              status: 'active',
            }])
            .select()
            .single();

          if (customerError) {
            console.error('Error al registrar cliente:', customerError);
            showToast('Error al registrar el cliente: ' + customerError.message, 'error');
            return;
          }

          if (newCustomer) {
            finalCustomerId = newCustomer.id;
            verifiedCustomer = newCustomer;
            await loadCustomers();
          }
        }
      }

      const saleStatus = formData.payment_method === 'Crédito' ? 'pending' : 'confirmed';
      const paymentStatus = formData.payment_method === 'Crédito' ? 'pending' : 'paid';

      const { data: saleData, error: saleError } = await supabase
        .from('sales')
        .insert([{
          customer_id: finalCustomerId || null,
          customer_name: saleCustomerName,
          customer_type: formData.customer_type,
          customer_phone: verifiedCustomer?.phone || formData.customer_phone?.trim() || null,
          quantity: formData.quantity,
          unit_price: formData.unit_price,
          total_amount,
          payment_method: formData.payment_method,
          payment_status: paymentStatus,
          payment_due_date: formData.payment_method === 'Crédito' ? formData.payment_due_date : null,
          notes: formData.notes?.trim() || null,
          source: formData.source,
          status: saleStatus,
          sale_date: new Date().toISOString(),
        }])
        .select();

      if (saleError) {
        console.error('Error al registrar venta:', saleError);
        showToast('Error al registrar la venta: ' + saleError.message, 'error');
        return;
      }

      if (saleData) {
        const newTotalBottles = (customerBottleCount || 0) + formData.quantity;
        const prevCycles = Math.floor((customerBottleCount || 0) / 5);
        const newCycles = Math.floor(newTotalBottles / 5);
        const cyclesEarned = newCycles - prevCycles;
        const rewardTriggered = formData.customer_id && customerBottleCount !== null && cyclesEarned > 0;

        // Limpiar campos de venta pero mantener cliente para posible siguiente venta
        if (formData.customer_id) {
          setModalSearchTerm('');
          setModalFilteredCustomers([]);
          setFormData((prev) => ({
            ...prev,
            quantity: prev.customer_type === 'store' ? 10 : 1,
            payment_method: 'Efectivo',
            payment_status: 'paid',
            payment_due_date: '',
            notes: '',
            source: 'manual',
          }));
          // Recalcular conteo con la venta recién guardada
          await countCustomerBottles(formData.customer_id);
        } else {
          // Cliente ocasional sin ID: limpiar todo
          setModalSearchTerm('');
          setModalFilteredCustomers([]);
          setCustomerBottleCount(null);
          setIsLoadingBottleCount(false);
          setFormData({
            customer_id: '',
            customer_name: '',
            customer_type: 'occasional',
            quantity: 1,
            unit_price: 17000,
            payment_method: 'Efectivo',
            payment_status: 'paid',
            payment_due_date: '',
            notes: '',
            source: 'manual',
            register_as_customer: false,
            customer_phone: '',
            customer_email: '',
            customer_address: '',
            customer_document_id: '',
          });
        }

        if (rewardTriggered) {
          setShowRewardModal({
            customerName: saleCustomerName,
            totalBottles: newTotalBottles,
            customerId: formData.customer_id,
          });
        } else if (formData.customer_id) {
          setShowNextSaleBanner({
            customerName: saleCustomerName,
            customerId: formData.customer_id,
          });
        } else {
          setShowModal(false);
        }

        setFilterStatus('all');
        setSearchQuery('');
        setFilterType('all');
        setFilterPaymentMethod('all');
        setFilterDate('');
        setCurrentPage(1);
        await loadStats();
        await loadSalesWithPagination();
        await checkPendingPayments();
        showToast('¡Venta registrada exitosamente!', 'success');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSale = async (saleId: string) => {
    const { error } = await supabase.from('sales').update({ status: 'confirmed' }).eq('id', saleId);
    if (!error) {
      showToast('Pedido confirmado exitosamente', 'success');
      await loadStats();
      await loadSalesWithPagination();
    } else {
      showToast('Error al confirmar el pedido: ' + error.message, 'error');
    }
  };

  const handleCancelSale = async (saleId: string) => {
    const { error } = await supabase.from('sales').update({ status: 'cancelled' }).eq('id', saleId);
    if (!error) {
      showToast('Pedido cancelado', 'warning');
      await loadStats();
      await loadSalesWithPagination();
    } else {
      showToast('Error al cancelar el pedido: ' + error.message, 'error');
    }
  };

  const handleMarkAsPaid = async (saleId: string) => {
    const { error } = await supabase.from('sales').update({ payment_status: 'paid' }).eq('id', saleId);
    if (!error) {
      await loadStats();
      await loadSalesWithPagination();
      await checkPendingPayments();
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmModal) return;

    const { error } = await supabase
      .from('sales')
      .delete()
      .eq('id', deleteConfirmModal.id);

    if (!error) {
      showToast('Venta eliminada exitosamente', 'success');
      setDeleteConfirmModal(null);
      await loadStats();
      await loadSalesWithPagination();
      await checkPendingPayments();
    } else {
      showToast('Error al eliminar la venta: ' + error.message, 'error');
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const statsLabelToday = filterPeriod === 'today' ? 'Ventas Hoy' : filterPeriod === 'week' ? 'Ventas Esta Semana' : filterPeriod === 'month' ? 'Ventas del Mes' : filterDate ? 'Ventas del Día' : 'Ventas Hoy';
  const statsLabelTotal = filterDate ? 'Ventas del Día' : 'Total Ventas en Sistema';
  const statsLabelBottles = filterPeriod === 'today' ? 'Botellones Hoy' : filterPeriod === 'week' ? 'Botellones Esta Semana' : filterPeriod === 'month' ? 'Botellones del Mes' : filterDate ? 'Botellones del Día' : 'Botellones Vendidos';
  const totalPages = Math.ceil(totalSales / ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSale(null);
    setModalSearchTerm('');
    setModalFilteredCustomers([]);
    setCustomerBottleCount(null);
    setIsLoadingBottleCount(false);
    setShowNextSaleBanner(null);
    setFormData({
      customer_id: '',
      customer_name: '',
      customer_type: 'occasional',
      quantity: 1,
      unit_price: 17000,
      payment_method: 'Efectivo',
      payment_status: 'paid',
      payment_due_date: '',
      notes: '',
      source: 'manual',
      register_as_customer: false,
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      customer_document_id: '',
    });
  };

  const openRewardFromTable = (customerId: string, customerName: string) => {
    setShowRewardModal({
      customerName,
      totalBottles: customerPromoCounts[customerId] || 5,
      customerId,
    });
  };

  const handleRegisterFreeBottle = async () => {
    if (!showRewardModal) return;
    setIsSubmitting(true);
    try {
      const customer = customers.find((c) => c.id === showRewardModal.customerId);
      const { error } = await supabase.from('sales').insert([{
        customer_id: showRewardModal.customerId,
        customer_name: showRewardModal.customerName,
        customer_type: customer?.customer_type || 'occasional',
        customer_phone: customer?.phone || null,
        quantity: 1,
        unit_price: 0,
        total_amount: 0,
        payment_method: 'Efectivo',
        payment_status: 'paid',
        notes: 'Botellón gratis - Promoción 5+1',
        source: 'manual',
        status: 'confirmed',
        sale_date: new Date().toISOString(),
        counts_toward_promotion: false,
      }]);

      if (error) {
        showToast('Error al registrar botellón gratis: ' + error.message, 'error');
        return;
      }

      // Marcar los 5 botellones de este ciclo como recompensados para reiniciar el contador
      const { data: cycleBottles } = await supabase
        .from('sales')
        .select('id')
        .eq('customer_id', showRewardModal.customerId)
        .eq('counts_toward_promotion', true)
        .gt('total_amount', 0)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false })
        .limit(5);

      if (cycleBottles && cycleBottles.length > 0) {
        const idsToReset = cycleBottles.map((s) => s.id);
        await supabase
          .from('sales')
          .update({ counts_toward_promotion: false })
          .in('id', idsToReset);
      }

      setShowRewardModal(null);
      // Mantener cliente y mostrar banner para siguiente venta
      setShowNextSaleBanner({
        customerName: showRewardModal.customerName,
        customerId: showRewardModal.customerId,
      });
      await loadStats();
      await loadSalesWithPagination();
      showToast('¡Botellón gratis registrado exitosamente!', 'success');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForNextSale = (keepCustomer: boolean = false) => {
    setShowNextSaleBanner(null);
    if (keepCustomer) {
      setModalSearchTerm('');
      setModalFilteredCustomers([]);
      setFormData((prev) => ({
        ...prev,
        quantity: prev.customer_type === 'store' ? 10 : 1,
        payment_method: 'Efectivo',
        payment_status: 'paid',
        payment_due_date: '',
        notes: '',
        source: 'manual',
      }));
    } else {
      setModalSearchTerm('');
      setModalFilteredCustomers([]);
      setCustomerBottleCount(null);
      setIsLoadingBottleCount(false);
      setFormData({
        customer_id: '',
        customer_name: '',
        customer_type: 'occasional',
        quantity: 1,
        unit_price: 17000,
        payment_method: 'Efectivo',
        payment_status: 'paid',
        payment_due_date: '',
        notes: '',
        source: 'manual',
        register_as_customer: false,
        customer_phone: '',
        customer_email: '',
        customer_address: '',
        customer_document_id: '',
      });
    }
  };

  const continueWithSameCustomer = (customerId: string, customerName: string) => {
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) return;
    let price = 17000;
    if (customer.customer_type === 'store') price = 12000;
    if (customer.customer_type === 'occasional_18000') price = 18000;
    setFormData({
      customer_id: customer.id,
      customer_name: customer.name,
      customer_type: customer.customer_type,
      unit_price: price,
      quantity: customer.customer_type === 'store' ? 10 : 1,
      payment_method: 'Efectivo',
      payment_status: 'paid',
      payment_due_date: '',
      notes: '',
      source: 'manual',
      register_as_customer: false,
      customer_phone: customer.phone || '',
      customer_email: customer.email || '',
      customer_address: customer.address || '',
      customer_document_id: customer.document_id || '',
    });
    setModalSearchTerm(customer.name);
    setModalFilteredCustomers([]);
    setShowNextSaleBanner(null);
    countCustomerBottles(customer.id);
  };

  const openNewSaleForCustomer = (customer: any) => {
    // Si viene de ventas históricas sin ID de customer, abrir como ocasional
    if (!customer.id) {
      setEditingSale(null);
      setModalSearchTerm('');
      setModalFilteredCustomers([]);
      setShowNextSaleBanner(null);
      setFormData({
        customer_id: '',
        customer_name: customer.name || '',
        customer_type: 'occasional',
        quantity: 1,
        unit_price: 17000,
        payment_method: 'Efectivo',
        payment_status: 'paid',
        payment_due_date: '',
        notes: '',
        source: 'manual',
        register_as_customer: false,
        customer_phone: customer.phone || '',
        customer_email: '',
        customer_address: '',
        customer_document_id: '',
      });
      setPageSearchTerm('');
      setPageFilteredCustomers([]);
      setCustomerSearchOpen(false);
      setCustomerBottleCount(null);
      setShowModal(true);
      countBottlesByNamePhone(customer.name || '', customer.phone || '');
      return;
    }

    let price = 17000;
    if (customer.customer_type === 'store') price = 12000;
    if (customer.customer_type === 'occasional_18000') price = 18000;
    setEditingSale(null);
    setModalSearchTerm('');
    setModalFilteredCustomers([]);
    setShowNextSaleBanner(null);
    setFormData({
      customer_id: customer.id,
      customer_name: customer.name,
      customer_type: customer.customer_type,
      quantity: customer.customer_type === 'store' ? 10 : 1,
      unit_price: price,
      payment_method: 'Efectivo',
      payment_status: 'paid',
      payment_due_date: '',
      notes: '',
      source: 'manual',
      register_as_customer: false,
      customer_phone: customer.phone || '',
      customer_email: customer.email || '',
      customer_address: customer.address || '',
      customer_document_id: customer.document_id || '',
    });
    setPageSearchTerm('');
    setPageFilteredCustomers([]);
    setCustomerSearchOpen(false);
    setCustomerBottleCount(null);
    setShowModal(true);
    countCustomerBottles(customer.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminLayout currentPage="sales">
      {/* Alertas de pagos pendientes */}
      {pendingPayments.length > 0 && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <i className="ri-alarm-warning-line text-red-600 text-2xl mt-0.5"></i>
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-2">⚠️ Pagos Pendientes Vencidos ({pendingPayments.length})</h3>
              <div className="space-y-2">
                {pendingPayments.map((payment) => (
                  <div key={payment.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{payment.customer_name}</p>
                      <p className="text-sm text-gray-600">
                        Monto: {formatCurrency(payment.total_amount)} • Vencimiento:{' '}
                        {new Date(payment.payment_due_date).toLocaleDateString('es-CO')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleMarkAsPaid(payment.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap text-sm"
                    >
                      Marcar como Pagado
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Ventas</h2>
          <div className="relative">
            <NotificationBell onClick={handleBellClick} />
            {/* Panel de notificaciones dropdown */}
            {showNotificationPanel && (
              <div 
                className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-teal-50 to-white">
                  <div className="flex items-center gap-2">
                    <i className="ri-notification-3-line text-teal-700 text-lg"></i>
                    <h3 className="font-bold text-gray-900">Pedidos Pendientes</h3>
                  </div>
                  <button 
                    onClick={() => setShowNotificationPanel(false)}
                    className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto">
                  {loadingNotifications ? (
                    <div className="p-8 text-center">
                      <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                      <p className="text-sm text-gray-500">Cargando pedidos...</p>
                    </div>
                  ) : pendingSalesList.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-check-double-line text-gray-400 text-3xl"></i>
                      </div>
                      <p className="text-gray-600 font-medium">No hay pedidos pendientes</p>
                      <p className="text-sm text-gray-500 mt-1">Todos los pedidos están al día</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {pendingSalesList.map((sale) => (
                        <div key={sale.id} className="p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-shopping-bag-line text-teal-700 text-sm"></i>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">{sale.customer_name}</p>
                                <p className="text-xs text-gray-500">{sale.customer_phone}</p>
                              </div>
                            </div>
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-bold rounded-full uppercase">
                              Pendiente
                            </span>
                          </div>
                          <div className="ml-10 space-y-1">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">{sale.quantity}</span> botellón(es) — {' '}
                              <span className="font-semibold text-teal-600">
                                ${sale.total_amount.toLocaleString('es-CO')}
                              </span>
                            </p>
                            {sale.delivery_address && (
                              <p className="text-xs text-gray-500">
                                <i className="ri-map-pin-line mr-1"></i>
                                {sale.delivery_address}
                              </p>
                            )}
                            {sale.delivery_day && (
                              <p className="text-xs text-gray-500">
                                <i className="ri-time-line mr-1"></i>
                                Entrega: {sale.delivery_day === 'today' ? 'Hoy' : 'Mañana'} a las {sale.delivery_time}
                              </p>
                            )}
                            <p className="text-[10px] text-gray-400">
                              Recibido: {formatDateTime(sale.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {pendingSalesList.length > 0 && (
                  <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={filterByPending}
                      className="block w-full text-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-sm font-medium cursor-pointer"
                    >
                      Ver todos filtrados
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Registra y administra todas las ventas</p>
        </div>
        <button
          onClick={() => {
            setEditingSale(null);
            setModalSearchTerm('');
            setModalFilteredCustomers([]);
            setCustomerBottleCount(null);
            setIsLoadingBottleCount(false);
            setShowNextSaleBanner(null);
            setFormData({
              customer_id: '',
              customer_name: '',
              customer_type: 'occasional',
              quantity: 1,
              unit_price: 17000,
              payment_method: 'Efectivo',
              payment_status: 'paid',
              payment_due_date: '',
              notes: '',
              source: 'manual',
              register_as_customer: false,
              customer_phone: '',
              customer_email: '',
              customer_address: '',
              customer_document_id: '',
            });
            setShowModal(true);
          }}
          className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm sm:text-base"
        >
          <i className="ri-add-line text-xl"></i>
          Nueva Venta
        </button>
      </div>

      {/* Buscador rápido de cliente */}
      <div className="mb-4 sm:mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <div className="flex-1 min-w-0 relative"
          >
            <div className="w-10 h-10 absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none"
            >
              <i className="ri-user-search-line text-gray-400 text-lg"></i>
            </div>
            <input
              type="text"
              placeholder="Buscar cliente por nombre, teléfono o cédula..."
              value={customerSearchOpen ? pageSearchTerm : ''}
              onChange={(e) => {
                setPageSearchTerm(e.target.value);
                if (!customerSearchOpen) setCustomerSearchOpen(true);
              }}
              onFocus={() => setCustomerSearchOpen(true)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
            />
            {customerSearchOpen && pageSearchTerm.trim() !== '' && pageFilteredCustomers.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {pageFilteredCustomers.map((customer, idx) => (
                  <div
                    key={customer.id || `sale-${idx}`}
                    className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{customer.name}</p>
                      <p className="text-xs text-gray-500">
                        {customer.phone || 'Sin teléfono'} &bull; {customer.customer_type === 'store' ? 'Tienda' : customer.customer_type === 'occasional_18000' ? 'Ocasional ($18k)' : 'Ocasional'}
                        {customer.source === 'sale' && (
                          <span className="ml-1 text-orange-600 font-medium">(cliente de ventas)</span>
                        )}
                      </p>
                      {customer.address && (
                        <p className="text-xs text-gray-400 truncate">{customer.address}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openNewSaleForCustomer(customer)}
                      className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 flex-shrink-0"
                    >
                      <i className="ri-add-line"></i>
                      Nueva venta
                    </button>
                  </div>
                ))}
              </div>
            )}
            {customerSearchOpen && pageSearchTerm.trim() !== '' && pageFilteredCustomers.length === 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center"
              >
                <p className="text-sm text-gray-500">No se encontraron clientes</p>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setCustomerSearchOpen(false);
              setPageSearchTerm('');
              setPageFilteredCustomers([]);
            }}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
          >
            Limpiar
          </button>
        </div>

        {/* Filtros de período rápido */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500 mr-1">Filtrar por:</span>
          <button
            onClick={() => handlePeriodFilterChange('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              filterPeriod === 'all'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todo
          </button>
          <button
            onClick={() => handlePeriodFilterChange('today')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              filterPeriod === 'today'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Hoy
          </button>
          <button
            onClick={() => handlePeriodFilterChange('week')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              filterPeriod === 'week'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Esta semana
          </button>
          <button
            onClick={() => handlePeriodFilterChange('month')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              filterPeriod === 'month'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Mes
          </button>
          {filterPeriod !== 'all' && periodStartDate && (
            <span className="text-xs text-gray-400 ml-1">
              {periodStartDate} {periodEndDate && periodEndDate !== periodStartDate ? `— ${periodEndDate}` : ''}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-xl sm:text-2xl text-green-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">{statsLabelToday}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(todaySales)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-line text-xl sm:text-2xl text-blue-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Ventas del Mes</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(monthSales)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-shopping-bag-line text-xl sm:text-2xl text-purple-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">{statsLabelTotal}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{totalSalesGlobal}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-box-3-line text-xl sm:text-2xl text-yellow-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">{statsLabelBottles}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{filterPeriod !== 'all' ? todayBottles : totalBottles}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-4 sm:mb-6 flex flex-col md:flex-row gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="relative">
            <div className="w-10 h-10 absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-lg"></i>
            </div>
            <input
              type="text"
              placeholder="Buscar por cliente..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 min-w-0">
          <div className="relative w-full md:w-auto">
            <input
              type="date"
              value={filterDate}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm font-medium cursor-pointer w-full md:w-auto"
            />
            {filterDate && (
              <button
                onClick={() => handleDateFilterChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
                title="Limpiar fecha"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
          <select
            value={filterType}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm font-medium whitespace-nowrap cursor-pointer w-full md:w-auto"
          >
            <option value="all">Todos los tipos</option>
            <option value="occasional">Ocasionales</option>
            <option value="occasional_18000">Ocasionales ($18k)</option>
            <option value="store">Tiendas</option>
          </select>
          <select
            value={filterPaymentMethod}
            onChange={(e) => handlePaymentFilterChange(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm font-medium whitespace-nowrap cursor-pointer w-full md:w-auto"
          >
            <option value="all">Todos los métodos</option>
            <option value="cash">Efectivo</option>
            <option value="transfer">Transferencia</option>
            <option value="credit">Crédito</option>
          </select>
        </div>
      </div>

      {/* Sales Table - Desktop */}
      <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cantidad / Promo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sales.map((sale) => (
                <tr
                  key={sale.id}
                  className="hover:bg-gray-50"
                  title={`Fecha: ${new Date(sale.created_at).toLocaleDateString('es-CO')}`}
                >                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{sale.customer_name}</div>
                    <div className="text-sm text-gray-500">{sale.customer_phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        sale.customer_type === 'store'
                          ? 'bg-purple-100 text-purple-800'
                          : sale.customer_type === 'occasional_18000'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {sale.customer_type === 'store'
                        ? 'Tienda'
                        : sale.customer_type === 'occasional_18000'
                        ? 'Ocasional ($18k)'
                        : 'Ocasional'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900 text-sm">
                      {sale.quantity} {sale.quantity === 1 ? 'botellón' : 'botellones'}
                    </div>
                    {sale.customer_id && customerPromoCounts[sale.customer_id] !== undefined && (() => {
                      const total = customerPromoCounts[sale.customer_id];
                      const mod = total % 5;
                      const isCycleComplete = mod === 0 && total > 0;
                      const filled = isCycleComplete ? 5 : mod;
                      const remaining = isCycleComplete ? 0 : 5 - mod;
                      return (
                        <div className="mt-2">
                          <div className="flex items-center gap-0.5 mb-1">
                            {[1,2,3,4,5].map(i => (
                              <div
                                key={i}
                                className={`w-4 h-1.5 rounded-full ${
                                  i <= filled ? 'bg-teal-500' : 'bg-gray-200'
                                }`}
                              ></div>
                            ))}
                            <span className="text-xs text-gray-400 ml-1">{filled}/5</span>
                          </div>
                          {isCycleComplete ? (
                            <button
                              type="button"
                              onClick={() => openRewardFromTable(sale.customer_id, sale.customer_name)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                            >
                              <i className="ri-gift-2-line"></i> Registrar gratis
                            </button>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Faltan <span className="font-semibold text-gray-700">{remaining}</span> para el gratis
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{formatCurrency(sale.total_amount)}</td>
                  <td className="px-6 py-4">
                    {sale.notes ? (
                      <div className="max-w-[200px] truncate text-sm text-gray-600" title={sale.notes}>
                        <i className="ri-sticky-note-line text-yellow-500 mr-1.5"></i>
                        {sale.notes}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        sale.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : sale.status === 'pending'
                          ? 'bg-orange-100 text-orange-800'
                          : sale.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {sale.status === 'confirmed'
                        ? 'Confirmado'
                        : sale.status === 'pending'
                        ? 'Pendiente'
                        : sale.status === 'cancelled'
                        ? 'Cancelado'
                        : sale.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      {sale.status === 'pending' && (
                        <button
                          onClick={() => handleConfirmSale(sale.id)}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                          title="Confirmar pedido"
                        >
                          <i className="ri-check-line mr-1"></i>
                          Confirmar
                        </button>
                      )}
                      {sale.status === 'pending' && (
                        <button
                          onClick={() => handleCancelSale(sale.id)}
                          className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer"
                          title="Cancelar pedido"
                        >
                          <i className="ri-close-line mr-1"></i>
                          Cancelar
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(sale)}
                        className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => setDeleteConfirmModal(sale)}
                        className="text-red-600 hover:text-red-700 font-medium cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Mostrando {sales.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} -{' '}
            {Math.min(currentPage * ITEMS_PER_PAGE, totalSales)} de {totalSales} ventas
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="ri-arrow-left-s-line"></i>
              Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página {currentPage} de {totalPages || 1}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                currentPage === totalPages || totalPages === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Siguiente
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Sales Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {sales.map((sale) => (
          <div
            key={sale.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
            title={`Fecha: ${new Date(sale.created_at).toLocaleDateString('es-CO')}`}
          >            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{sale.customer_name}</h3>
                <p className="text-sm text-gray-600">{sale.customer_phone}</p>
              </div>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ml-2 ${
                  sale.customer_type === 'store'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {sale.customer_type === 'store'
                  ? 'Tienda'
                  : 'Ocasional'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Cantidad</p>
                <p className="font-semibold text-gray-900">{sale.quantity} {sale.quantity === 1 ? 'botellón' : 'botellones'}</p>
                {sale.customer_id && customerPromoCounts[sale.customer_id] !== undefined && (() => {
                  const total = customerPromoCounts[sale.customer_id];
                  const mod = total % 5;
                  const isCycleComplete = mod === 0 && total > 0;
                  const filled = isCycleComplete ? 5 : mod;
                  const remaining = isCycleComplete ? 0 : 5 - mod;
                  return (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">Promo 5+1</p>
                      <div className="flex items-center gap-0.5 mb-1">
                        {[1,2,3,4,5].map(i => (
                          <div key={i} className={`w-4 h-1.5 rounded-full ${i <= filled ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
                        ))}
                        <span className="text-xs text-gray-400 ml-1">{filled}/5</span>
                      </div>
                      {isCycleComplete ? (
                        <button
                          type="button"
                          onClick={() => openRewardFromTable(sale.customer_id, sale.customer_name)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                        >
                          <i className="ri-gift-2-line"></i> Registrar gratis
                        </button>
                      ) : (
                        <p className="text-xs text-gray-500">Faltan <span className="font-semibold text-gray-700">{remaining}</span> para el gratis</p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <p className="text-xs text-gray-600">Total</p>
                <p className="font-semibold text-teal-600">{formatCurrency(sale.total_amount)}</p>
              </div>
            </div>

            {sale.notes && (
              <div className="mb-3 bg-yellow-50 border border-yellow-100 rounded-lg p-2.5">
                <p className="text-xs text-yellow-700 line-clamp-2" title={sale.notes}>
                  <i className="ri-sticky-note-line text-yellow-500 mr-1"></i>
                  {sale.notes}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    sale.status === 'confirmed'
                      ? 'bg-green-100 text-green-800'
                      : sale.status === 'pending'
                      ? 'bg-orange-100 text-orange-800'
                      : sale.status === 'cancelled'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {sale.status === 'confirmed'
                    ? 'Confirmado'
                    : sale.status === 'pending'
                    ? 'Pendiente'
                    : sale.status === 'cancelled'
                    ? 'Cancelado'
                    : sale.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {sale.status === 'pending' && (
                  <button
                    onClick={() => handleConfirmSale(sale.id)}
                    className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    title="Confirmar pedido"
                  >
                    <i className="ri-check-line mr-1"></i>
                    Confirmar
                  </button>
                )}
                {sale.status === 'pending' && (
                  <button
                    onClick={() => handleCancelSale(sale.id)}
                    className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer"
                    title="Cancelar pedido"
                  >
                    <i className="ri-close-line mr-1"></i>
                    Cancelar
                  </button>
                )}
                <button
                  onClick={() => handleEdit(sale)}
                  className="text-teal-600 hover:text-teal-700 cursor-pointer"
                  title="Editar"
                >
                  <i className="ri-edit-line text-lg"></i>
                </button>
                <button
                  onClick={() => setDeleteConfirmModal(sale)}
                  className="text-red-600 hover:text-red-700 cursor-pointer"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Mobile Pagination */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 text-center mb-3">
            Página {currentPage} de {totalPages || 1}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm ${
                currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm ${
                currentPage === totalPages || totalPages === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingSale ? 'Editar Venta' : 'Nueva Venta'}
              </h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Banner: siguiente venta al mismo cliente */}
            {showNextSaleBanner && (
              <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-600 text-lg"></i>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">
                    Venta guardada. ¿Registrar otra venta a <strong>{showNextSaleBanner.customerName}</strong>?
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => continueWithSameCustomer(showNextSaleBanner.customerId, showNextSaleBanner.customerName)}
                    className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sí, continuar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNextSaleBanner(null);
                      setShowModal(false);
                      resetForNextSale(false);
                    }}
                    className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Buscar cliente existente */}
              {formData.customer_type !== 'occasional' && formData.customer_type !== 'occasional_18000' && !formData.register_as_customer && !editingSale && (
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Buscar Cliente</label>
                  <input
                    type="text"
                    placeholder="Escribe nombre, teléfono o cédula..."
                    value={modalSearchTerm}
                    onChange={(e) => setModalSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                  {modalFilteredCustomers.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {modalFilteredCustomers.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => handleCustomerSelect(customer)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 cursor-pointer"
                        >
                          <p className="font-medium text-gray-900">{customer.name}</p>
                          <p className="text-sm text-gray-500">{customer.phone} &bull; {customer.customer_type === 'store' ? 'Tienda' : customer.customer_type === 'occasional_18000' ? 'Ocasional ($18k)' : 'Ocasional'}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Conteo de botellones acumulados */}
              {(formData.customer_id || (formData.customer_name && formData.customer_phone)) && (
                <div className={`mt-3 rounded-lg p-3 flex items-center gap-3 ${
                  customerBottleCount !== null && customerBottleCount > 0 && customerBottleCount % 5 === 0
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    customerBottleCount !== null && customerBottleCount > 0 && customerBottleCount % 5 === 0
                      ? 'bg-green-100'
                      : 'bg-blue-100'
                  }`}>
                    {isLoadingBottleCount ? (
                      <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <i className={`${
                        customerBottleCount !== null && customerBottleCount > 0 && customerBottleCount % 5 === 0
                          ? 'ri-gift-line text-green-600'
                          : 'ri-drop-line text-blue-600'
                      } text-lg`}></i>
                    )}
                  </div>
                  <div className="flex-1">
                    {isLoadingBottleCount ? (
                      <p className="text-sm font-medium text-gray-800">Calculando botellones acumulados...</p>
                    ) : customerBottleCount !== null ? (
                      <>
                        <p className="text-sm font-medium text-gray-800">
                          {customerBottleCount > 0 && customerBottleCount % 5 === 0
                            ? `¡${formData.customer_name} tiene ${customerBottleCount} botellones y YA le corresponde 1 GRATIS!`
                            : `${formData.customer_name} lleva ${customerBottleCount} botellones comprados.`}
                        </p>
                        {customerBottleCount % 5 !== 0 && (
                          <p className="text-xs text-gray-600 mt-0.5">
                            {(() => {
                              const remaining = 5 - (customerBottleCount % 5);
                              return `Le falta${remaining === 1 ? '' : 'n'} ${remaining} botellón${remaining === 1 ? '' : 'es'} para el próximo gratis.`;
                            })()}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm font-medium text-gray-800">No se pudo cargar el conteo de botellones</p>
                    )}
                  </div>
                </div>
              )}

              {/* Checkbox: Registrar nuevo cliente */}
              {formData.customer_type !== 'occasional' && formData.customer_type !== 'occasional_18000' && !editingSale && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="registerCustomer"
                    checked={formData.register_as_customer}
                    onChange={(e) => setFormData({ ...formData, register_as_customer: e.target.checked })}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-600"
                  />
                  <label htmlFor="registerCustomer" className="text-sm text-gray-700 cursor-pointer">
                    Registrar como nuevo cliente
                  </label>
                </div>
              )}

              {/* Tipo de cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Cliente *</label>
                <select
                  value={formData.customer_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    let price = 17000;
                    if (type === 'store') price = 12000;
                    if (type === 'occasional_18000') price = 18000;
                    setFormData({
                      ...formData,
                      customer_type: type,
                      unit_price: price,
                      quantity: type === 'store' ? 10 : 1,
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="occasional">Ocasional — $17,000 COP</option>
                  <option value="occasional_18000">Ocasional — $18,000 COP</option>
                  <option value="store">Tienda — $12,000 COP (mín. 10 botellones)</option>
                </select>
              </div>

              {/* Nombre del cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.customer_type === 'occasional' || formData.customer_type === 'occasional_18000' ? 'Nombre del Cliente *' : 'Nombre Completo *'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                />
              </div>

              {/* Teléfono */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
                <input
                  type="tel"
                  required
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                />
              </div>

{/* Dirección */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                <textarea
                  value={formData.customer_address}
                  onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                ></textarea>
              </div>

{/* Cantidad y Precio */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad *</label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                  {formData.customer_type === 'store' && (
                    <p className="text-xs text-gray-500 mt-1">Mínimo 10 botellones para precio de tienda</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Precio Unitario (COP)</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Total */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total a Pagar</p>
                <p className="text-2xl font-bold text-teal-600">
                  {formatCurrency(formData.quantity * formData.unit_price)}
                </p>
              </div>

              {/* Método de pago */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago *</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => {
                    const method = e.target.value;
                    setFormData({
                      ...formData,
                      payment_method: method,
                      payment_status: method === 'Crédito' ? 'pending' : 'paid',
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Crédito">Crédito</option>
                </select>
              </div>



              {/* Fecha de vencimiento */}
              {formData.payment_method === 'Crédito' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Pago *</label>
                  <input
                    type="date"
                    required={formData.payment_method === 'Crédito'}
                    value={formData.payment_due_date}
                    onChange={(e) => setFormData({ ...formData, payment_due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  />
                </div>
              )}

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                ></textarea>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    editingSale ? 'Actualizar Venta' : 'Registrar Venta'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Confirmar Eliminación</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-2">¿Estás seguro de que deseas eliminar esta venta?</p>
              <p className="font-semibold text-gray-900">{deleteConfirmModal.customer_name}</p>
              <p className="text-sm text-gray-500">
                {formatCurrency(deleteConfirmModal.total_amount)} • {deleteConfirmModal.quantity} botellones
              </p>
              <p className="text-sm text-red-600 mt-4">Esta acción no se puede deshacer.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Eliminar
              </button>
              <button
                onClick={() => setDeleteConfirmModal(null)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reward Modal - Promoción 5+1 */}
      {showRewardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-8 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">¡Premio Ganado!</h3>
              <p className="text-teal-100 text-sm">Promoción 5 + 1 gratis</p>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-800 font-semibold text-lg mb-1">{showRewardModal.customerName}</p>
              <p className="text-gray-600 text-sm mb-4">
                Completó <strong>{showRewardModal.totalBottles} botellones</strong> comprados.
                Le corresponde <strong>1 botellón GRATIS</strong>.
              </p>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-teal-700">¿Deseas registrar el botellón gratis en el sistema ahora?</p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleRegisterFreeBottle}
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60"
                >
                  {isSubmitting ? 'Registrando...' : 'Sí, registrar botellón gratis'}
                </button>
                <button
                  onClick={() => {
                    setShowRewardModal(null);
                    // Mostrar banner para registrar otra venta al mismo cliente
                    if (showRewardModal) {
                      setShowNextSaleBanner({
                        customerName: showRewardModal.customerName,
                        customerId: showRewardModal.customerId,
                      });
                    }
                  }}
                  className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Luego lo registro manualmente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast.visible && (
        <div
          className="fixed bottom-6 right-6 z-[60]"
          style={{
            animation: 'toastSlideIn 0.3s ease-out',
          }}
        >
          <div
            className={`flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg border ${
              toast.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : toast.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : toast.type === 'warning'
                ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : toast.type === 'error'
                ? 'bg-red-500 text-white'
                : toast.type === 'warning'
                ? 'bg-yellow-500 text-white'
                : 'bg-blue-500 text-white'
            }`}>
              {toast.type === 'success' && <i className="ri-check-line text-sm"></i>}
              {toast.type === 'error' && <i className="ri-close-line text-sm"></i>}
              {toast.type === 'warning' && <i className="ri-alert-line text-sm"></i>}
              {toast.type === 'info' && <i className="ri-information-line text-sm"></i>}
            </div>
            <p className="text-sm font-medium">{toast.message}</p>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}